import { canonicalStage } from '../lib/traceability';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const apiClient = axios.create({
  baseURL: API_URL,
  // #Fase3-G2 — La API exige proveedor únicamente a los clientes que declaran esta capacidad.
  headers: { 'Content-Type': 'application/json', 'X-Client-Capabilities': 'geo,document,provider' },
});

// Request interceptor
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('soem_access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Response interceptor with refresh token logic
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }
      originalRequest._retry = true;
      isRefreshing = true;
      const refreshToken = localStorage.getItem('soem_refresh_token');
      if (!refreshToken) {
        isRefreshing = false;
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(error);
      }
      try {
        const { data } = await axios.post(`${API_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        }, { headers: { 'X-Client-Capabilities': 'geo,document,provider' } });
        localStorage.setItem('soem_access_token', data.access_token);
        processQueue(null, data.access_token);
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;

// Auth
export const authAPI = {
  login: (email, password) => apiClient.post('/auth/login', { email, password }),
};

// Users
export const usersAPI = {
  list: (params) => apiClient.get('/users', { params }),
  get: (id) => apiClient.get(`/users/${id}`),
  // POST /users es el endpoint correcto: requiere super_admin, dispara
  // welcome email cuando send_credentials=true y acepta los campos
  // completos en una sola llamada. /auth/register es público y descarta
  // en silencio send_credentials, status, supervisor, etc.
  // (#jeixrC6i / #ZVAUKJ5l).
  create: (data) => apiClient.post('/users', data),
  update: (id, data) => apiClient.put(`/users/${id}`, data),
  changeStatus: (id, data) => apiClient.patch(`/users/${id}/status`, data),
  uploadPhoto: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/users/${id}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// #A10 — list() no aceptaba `params` (a diferencia de usersAPI/eventsAPI/
// etc.), así que cualquier {limit: ...} que un caller intentara pasar se
// descartaba en silencio y el backend aplicaba su default de 50
// registros. Con más de 50 clientes/proyectos/evaluaciones, el admin
// solo veía los 50 más recientes y los contadores/filtros mentían.
// Ahora se reenvían params y se sube el límite por defecto.
const LIST_ALL_PARAMS = { limit: 1000 };

// Clients
export const clientsAPI = {
  list: (params) => apiClient.get('/clients', { params: { ...LIST_ALL_PARAMS, ...params } }),
  create: (data) => apiClient.post('/clients', data),
  update: (id, data) => apiClient.put(`/clients/${id}`, data),
};

// Projects
export const projectsAPI = {
  list: (params) => apiClient.get('/projects', { params: { ...LIST_ALL_PARAMS, ...params } }),
  get: (id) => apiClient.get(`/projects/${id}`),
  create: (data) => apiClient.post('/projects', data),
  update: (id, data) => apiClient.put(`/projects/${id}`, data),
  // Asignar levantador / líder: el PUT /projects/{id} NO acepta el campo
  // `levantadores` (ProjectUpdate pydantic lo descarta sin error). Para
  // persistir hay que usar los endpoints dedicados (#hrTGmL — antes el
  // UI llamaba update con levantadores y los descartaba en silencio).
  assignLevantador: (id, userId) => apiClient.put(`/projects/${id}/assign-levantador`, { user_id: userId }),
  assignLeader: (id, userId) => apiClient.put(`/projects/${id}/assign-leader`, { user_id: userId }),
  getEvaluations: (id) => apiClient.get(`/projects/${id}/evaluations`),
  getEvents: (id) => apiClient.get(`/projects/${id}/events`),
  createEvent: (id, data) => apiClient.post(`/projects/${id}/events`, data),
  linkEvent: (projectId, eventId) => apiClient.post(`/projects/${projectId}/events/${eventId}/link`),
};

// Evaluations
export const evaluationsAPI = {
  list: (params) => apiClient.get('/evaluations', { params: { ...LIST_ALL_PARAMS, ...params } }),
  get: (id) => apiClient.get(`/evaluations/${id}`),
  // Acepta projectId solo (legacy) o un objeto con extras como
  // assigned_levantador_id, event_id, template_id (#WiVhw3i0 — antes
  // sólo enviaba project_id y el levantador asignado nunca se persistía).
  create: (projectIdOrPayload, extras = {}) => {
    const payload = typeof projectIdOrPayload === 'string'
      ? { project_id: projectIdOrPayload, ...extras }
      : projectIdOrPayload;
    return apiClient.post('/evaluations', { ...payload, evaluation_type: canonicalStage(payload.evaluation_type) });
  },
  update: (id, data) => apiClient.patch(`/evaluations/${id}`, data),
  updateItems: (id, items, generalFields) =>
    apiClient.put(`/evaluations/${id}/items`, { items, general_fields: generalFields }),
  updateGeneralFields: (id, generalFields) =>
    apiClient.put(`/evaluations/${id}/general-fields`, generalFields),
  submit: (id) => apiClient.post(`/evaluations/${id}/submit`),
  publish: (id) => apiClient.post(`/evaluations/${id}/publish`),
  forcePublish: (id) => apiClient.post(`/evaluations/${id}/force-publish`),
  returnToLevantador: (id, comments) =>
    apiClient.post(`/evaluations/${id}/return`, { comments }),
  // #Fase1-2 — Axios codifica la URL sin alterar el identificador del archivo.
  deleteEvidence: (id, criterioId, file_url) =>
    apiClient.delete(`/evaluations/${id}/items/${criterioId}/evidence`, { params: { file_url } }),
  getGeo: (id) => apiClient.get(`/evaluations/${id}/geo`),
  uploadEvidence: (id, criterioId, file, metadata = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(metadata).forEach(([key, value]) => {
      if (value != null) formData.append(key, value);
    });
    return apiClient.post(
      `/evaluations/${id}/items/${criterioId}/evidence`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  // #B1 — delete() apuntaba a DELETE /evaluations/{id}, método que el
  // backend no implementa (405; el borrado lógico real es
  // POST /{id}/archive). Sin callers hoy — se elimina en vez de dejar
  // código muerto apuntando a un método inexistente.
  regenerateReport: (id) => apiClient.post(`/evaluations/${id}/report/regenerate`),
  getReportPDF: (id) => apiClient.get(`/evaluations/${id}/report.pdf`, { responseType: 'blob' }),
};

// Events
export const eventsAPI = {
  getReportPDF: (id) => apiClient.get(`/events/${id}/report.pdf`, { responseType: 'blob' }),
  list: (params) => apiClient.get('/events', { params }),
  stats: () => apiClient.get('/events/stats'),
  get: (id) => apiClient.get(`/events/${id}`),
  create: (data) => apiClient.post('/events', data),
  update: (id, data) => apiClient.put(`/events/${id}`, data),
  changeStatus: (id, status) => apiClient.patch(`/events/${id}/status`, { status }),
  linkPreChecklist: (id, evalId) => apiClient.put(`/events/${id}/pre-checklist`, { evaluation_id: evalId }),
  linkPostChecklist: (id, evalId) => apiClient.put(`/events/${id}/post-checklist`, { evaluation_id: evalId }),
  getEvaluations: (id) => apiClient.get(`/events/${id}/evaluations`),
  getAvailableTemplates: (id) => apiClient.get(`/events/${id}/available-templates`),
  assignTemplate: (id, data) => apiClient.post(`/events/${id}/assign-template`, {
    ...data, evaluation_type: canonicalStage(data.evaluation_type),
  }),
  getNearbyServices: (id) => apiClient.get(`/events/${id}/nearby-services`),
  searchNearbyServices: (id, data) => apiClient.post(`/events/${id}/nearby-services/search`, data),
  saveNearbyServicesBulk: (id, services) => apiClient.post(`/events/${id}/nearby-services/bulk`, { services }),
  deleteNearbyService: (id, sid) => apiClient.delete(`/events/${id}/nearby-services/${sid}`),
  // #UbicacionUnify — endpoints map_points event-scoped + consolidado
  getMapPoints: (id) => apiClient.get(`/events/${id}/map-points`),
  createMapPoint: (id, data) => apiClient.post(`/events/${id}/map-points`, data),
  updateMapPoint: (id, pid, data) => apiClient.put(`/events/${id}/map-points/${pid}`, data),
  deleteMapPoint: (id, pid) => apiClient.delete(`/events/${id}/map-points/${pid}`),
  getMapAndServices: (id) => apiClient.get(`/events/${id}/map-and-services`),
  // #MapShapes — polygons + polylines scope-evento (Fase C)
  getMapShapes: (id) => apiClient.get(`/events/${id}/map-shapes`),
  createMapShape: (id, data) => apiClient.post(`/events/${id}/map-shapes`, data),
  updateMapShape: (id, sid, data) => apiClient.put(`/events/${id}/map-shapes/${sid}`, data),
  deleteMapShape: (id, sid) => apiClient.delete(`/events/${id}/map-shapes/${sid}`),
  // #MapReport — export PDF (Fase D). responseType: 'blob' para download.
  exportMapReportPdf: (id) => apiClient.get(`/events/${id}/map-report.pdf`, {
    responseType: 'blob',
  }),
};

// Emergency Services
export const emergencyServicesAPI = {
  list: (params) => apiClient.get('/emergency-services', { params }),
  stats: () => apiClient.get('/emergency-services/stats'),
  get: (id) => apiClient.get(`/emergency-services/${id}`),
  create: (data) => apiClient.post('/emergency-services', data),
  update: (id, data) => apiClient.put(`/emergency-services/${id}`, data),
};

// Authorities
export const authoritiesAPI = {
  list: (params) => apiClient.get('/authorities', { params }),
  stats: () => apiClient.get('/authorities/stats'),
  get: (id) => apiClient.get(`/authorities/${id}`),
  create: (data) => apiClient.post('/authorities', data),
  update: (id, data) => apiClient.put(`/authorities/${id}`, data),
};

// Emergency linking
export const emergencyLinksAPI = {
  link: (entityId, entityType, contextId, contextType) =>
    apiClient.post('/emergency-links', { entity_id: entityId, entity_type: entityType, context_id: contextId, context_type: contextType }),
  unlink: (entityId, contextId) =>
    apiClient.delete(`/emergency-links/${entityId}/${contextId}`),
  getLinks: (params) => apiClient.get('/emergency-links', { params })
    .then(r => ({ ...r, data: Array.isArray(r.data) ? r.data : (r.data.items || []) })),
};

// Locations
export const locationsAPI = {
  list: (params) => apiClient.get('/locations', { params }),
  stats: () => apiClient.get('/locations/stats'),
  search: (q, params) => apiClient.get('/locations/search', { params: { q, ...params } }),
  get: (id) => apiClient.get(`/locations/${id}`),
  create: (data) => apiClient.post('/locations', data),
  update: (id, data) => apiClient.put(`/locations/${id}`, data),
  changeStatus: (id, status) => apiClient.patch(`/locations/${id}/status`, { status }),
  getEvaluations: (id) => apiClient.get(`/locations/${id}/evaluations`),
  getProjects: (id) => apiClient.get(`/locations/${id}/projects`),
  getEvents: (id) => apiClient.get(`/locations/${id}/events`),
  uploadPhoto: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/locations/${id}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // #LocationPhotoMgmt — timeout largo para fotos grandes (default
      // axios = 0 = sin timeout, pero el browser puede abortar; 90s da
      // margen para fotos hasta ~10 MB en redes lentas)
      timeout: 90000,
    });
  },
  // #LocationPhotoMgmt — gestión de fotos: borrar por URL + reordenar
  deletePhoto: (id, url) =>
    apiClient.delete(`/locations/${id}/photos`, { params: { url } }),
  reorderPhotos: (id, photos) =>
    apiClient.patch(`/locations/${id}/photos/reorder`, { photos }),
  // #UbicacionUnify — endpoints location-level (paridad con event)
  getNearbyServices: (id) => apiClient.get(`/locations/${id}/nearby-services`),
  searchNearbyServices: (id, data) => apiClient.post(`/locations/${id}/nearby-services/search`, data),
  saveNearbyServicesBulk: (id, services) => apiClient.post(`/locations/${id}/nearby-services/bulk`, { services }),
  deleteNearbyService: (id, sid) => apiClient.delete(`/locations/${id}/nearby-services/${sid}`),
  getMapPoints: (id) => apiClient.get(`/locations/${id}/map-points`),
  createMapPoint: (id, data) => apiClient.post(`/locations/${id}/map-points`, data),
  updateMapPoint: (id, pid, data) => apiClient.put(`/locations/${id}/map-points/${pid}`, data),
  deleteMapPoint: (id, pid) => apiClient.delete(`/locations/${id}/map-points/${pid}`),
  // #MapShapes — polygons + polylines scope-venue (Fase C)
  getMapShapes: (id) => apiClient.get(`/locations/${id}/map-shapes`),
  createMapShape: (id, data) => apiClient.post(`/locations/${id}/map-shapes`, data),
  updateMapShape: (id, sid, data) => apiClient.put(`/locations/${id}/map-shapes/${sid}`, data),
  deleteMapShape: (id, sid) => apiClient.delete(`/locations/${id}/map-shapes/${sid}`),
  // #MapReport — export PDF (Fase D)
  exportMapReportPdf: (id) => apiClient.get(`/locations/${id}/map-report.pdf`, {
    responseType: 'blob',
  }),
  linkEmergencyService: (id, sid) => apiClient.post(`/locations/${id}/emergency-services/${sid}/link`),
  unlinkEmergencyService: (id, sid) => apiClient.delete(`/locations/${id}/emergency-services/${sid}/link`),
};

// Audit Logs
export const auditAPI = {
  list: (params) => apiClient.get('/audit-logs', { params }),
};

// Project metadata extensions
export const projectMetaAPI = {
  getAllowedTemplates: (pid) => apiClient.get(`/project-meta/${pid}/allowed-templates`)
    .then(r => {
      // Normalize: api returns { templates: [...] } or { allowed_template_ids: [...] }
      const ids = r.data.allowed_template_ids || r.data.templates?.map(t => typeof t === 'string' ? t : t.id) || [];
      return { ...r, data: { allowed_template_ids: ids } };
    }),
  setAllowedTemplates: (pid, ids) => apiClient.put(`/project-meta/${pid}/allowed-templates`, { template_ids: ids }),
  assignEvalTemplate: (pid, data) => apiClient.post(`/project-meta/${pid}/eval-template`, {
    ...data, evaluation_type: canonicalStage(data.evaluation_type),
  }),
  getEvalTemplates: (pid) => apiClient.get(`/project-meta/${pid}/eval-templates`)
    .then(r => ({ ...r, data: Array.isArray(r.data) ? r.data : (r.data.items || []) })),
};

// #Fase2-B1 — El catálogo y su orden pertenecen a la API.
export const zonesAPI = { list: () => apiClient.get('/catalog/zones') };
